import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Icon } from '../../components/Icon.js';
import { questionCount, type Chip, type ChipTone, type Step } from './logic.js';

const TONES: Record<ChipTone, { bg: string; fg: string }> = {
  info: { bg: 'var(--info-100)', fg: 'var(--info-500)' },
  ok: { bg: 'var(--success-100)', fg: 'var(--st-approved)' },
  warn: { bg: 'var(--warning-100)', fg: 'var(--text-1)' },
  plain: { bg: 'var(--bg-sunken)', fg: 'var(--text-2)' },
};

/** The notes under a question's wording. */
export function Chips({ chips }: { chips: Chip[] }) {
  if (!chips.length) return null;
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', margin: '-2px 0 8px' }}>
      {chips.map((c, i) => (
        <span
          key={i}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '2px 8px',
            borderRadius: 99,
            fontSize: 11.5,
            fontWeight: 600,
            background: TONES[c.tone].bg,
            color: TONES[c.tone].fg,
            maxWidth: '100%',
          }}
        >
          <Icon name={c.icon} size={12} style={{ flex: 'none' }} />
          <span>{c.text}</span>
        </span>
      ))}
    </div>
  );
}

/** Past this many steps the labels can't fit, so only the numbered circles show. */
const LABELS_UP_TO = 10;

/**
 * The steps as one row of numbered circles on a line, each named underneath
 * (the full name and count are in its tooltip and the step header). With
 * `onMove`, steps drag to reorder, or Alt + ← / → on a focused step; with
 * `onAdd`, "Add" is the last circle. The staff view has none selected.
 */
export function StepBar({
  steps,
  current,
  onPick,
  onMove,
  onAdd,
}: {
  steps: Step[];
  current: number | null;
  onPick: (i: number) => void;
  onMove?: (from: number, to: number) => void;
  onAdd?: () => void;
}) {
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const refocus = useRef<string | null>(null);
  // Questions before the first heading stay first (see moveStep).
  const fixedFirst = !steps[0]?.head;
  const canMove = (from: number, to: number) =>
    Boolean(onMove) && from !== to && to >= 0 && to < steps.length && !(fixedFirst && (from === 0 || to === 0));
  const keyOf = (s: Step, i: number) => s.head?.id ?? `step-${i}`;

  // Keep focus on a step moved with the keyboard.
  useEffect(() => {
    if (!refocus.current) return;
    listRef.current?.querySelector<HTMLElement>(`[data-step="${refocus.current}"]`)?.focus();
    refocus.current = null;
  });

  const clear = () => {
    setDrag(null);
    setOver(null);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>, i: number, key: string) => {
    if (e.target !== e.currentTarget) return;
    if (e.altKey && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const to = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? i - 1 : i + 1;
      if (!canMove(i, to)) return;
      refocus.current = key;
      onMove!(i, to);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onPick(i);
    }
  };

  return (
    <div className={`bld-steps${steps.length > LABELS_UP_TO ? ' no-labels' : ''}`}>
      <div ref={listRef} role="tablist" aria-label="Form steps" className="bld-steps-list">
        {steps.map((s, i) => {
          const on = i === current;
          const name = s.head?.label || 'Your details';
          const key = keyOf(s, i);
          const movable = Boolean(onMove) && !(fixedFirst && i === 0);
          const target = drag !== null && over === i && canMove(drag, i);
          return (
            <div
              key={key}
              data-step={key}
              role="tab"
              aria-selected={on}
              aria-label={`Step ${i + 1}: ${name}, ${questionCount(s.items.length)}`}
              title={`${name} · ${questionCount(s.items.length)}`}
              tabIndex={0}
              className={`bld-step${on ? ' on' : ''}`}
              draggable={movable}
              onClick={() => onPick(i)}
              onKeyDown={(e) => onKey(e, i, key)}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', key);
                setDrag(i);
              }}
              onDragOver={(e) => {
                if (drag === null) return;
                e.preventDefault();
                if (over !== i) setOver(i);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (drag !== null && canMove(drag, i)) onMove!(drag, i);
                clear();
              }}
              onDragEnd={clear}
              style={{
                opacity: drag === i ? 0.4 : 1,
                boxShadow: target
                  ? `inset ${drag! < i ? '-2px' : '2px'} 0 0 0 var(--jfh-fern-green)`
                  : undefined,
              }}
            >
              <span className="bld-step-dot">{i + 1}</span>
              <span className="bld-step-name">{name}</span>
            </div>
          );
        })}
      </div>
      {onAdd && (
        <button type="button" className="bld-step-add" onClick={onAdd} title="Add a step" aria-label="Add a step">
          <span className="bld-step-dot">
            <Icon name="plus" size={16} />
          </span>
          <span className="bld-step-name">Add</span>
        </button>
      )}
    </div>
  );
}

/**
 * The bar over the step being edited: "Step 2 of 7", its full name and count.
 * With `onEdit`, the step's own settings (title, line, show/hide rule) open
 * from here; `rule` says when the step is shown, if it has a rule.
 */
export function StepHead({
  n,
  total,
  name,
  count,
  rule,
  onEdit,
}: {
  n: number;
  total: number;
  name: string;
  count: number;
  rule?: string | null;
  onEdit?: () => void;
}) {
  return (
    <div className="bld-step-head">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span className="bld-step-head-eyebrow">
          Step {n} of {total}
        </span>
        <span className="bld-step-head-name">{name}</span>
        {rule && (
          <span className="bld-step-head-rule">
            <Icon name="filter" size={12} /> Only shown when {rule}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flex: 'none' }}>
        <span className="mono bld-step-head-count">{questionCount(count)}</span>
        {onEdit && (
          <button type="button" className="bld-step-head-edit" onClick={onEdit}>
            <Icon name="pencil" size={12} /> Edit step
          </button>
        )}
      </div>
    </div>
  );
}

/** The link card under the form that switches to (or back from) staff questions. */
export function StaffLink({ staffView, count, steps, onClick }: { staffView: boolean; count: number; steps: number; onClick: () => void }) {
  return (
    <button
      type="button"
      className="bld-staff-link"
      onClick={onClick}
      style={{
        marginTop: 16,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 'var(--r-lg)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        textAlign: 'left',
        color: 'var(--text-1)',
      }}
    >
      <span style={{ color: 'var(--jfh-cactus)' }}>
        <Icon name={staffView ? 'arrowLeft' : 'shield'} size={16} />
      </span>
      <span style={{ flex: 1 }}>
        <strong style={{ fontSize: 13.5 }}>{staffView ? 'Back to the applicant’s form' : `Staff-only questions (${count})`}</strong>
        <br />
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {staffView
            ? `${steps} ${steps === 1 ? 'step' : 'steps'}`
            : 'Filled in by JFH staff while approving. Applicants never see these.'}
        </span>
      </span>
      <Icon name="chevronR" size={15} />
    </button>
  );
}
